/**
 * Copyright © 2024-2026 Entangled Harmony Labs, LLC
 * All Rights Reserved. Proprietary and Confidential.
 */
/**
 * Tab Animation Controls Component
 *
 * UI controls for tab animation playback (play/pause/speed/etc.)
 */

import React from 'react';
import { Button } from '../ui/button';
import { Slider } from '../ui/slider';
import { Badge } from '../ui/badge';
import {
  Play,
  Pause,
  Square,
  SkipBack,
  Repeat,
  Gauge,
  X
} from 'lucide-react';
import { AnimationState, AnimationCommand } from '../../types/tabAnimation';

interface TabAnimationControlsProps {
  state: AnimationState;
  progress: number;
  dispatch: (command: AnimationCommand) => void;
  tempo?: number;
  onClose?: () => void;
}

const TabAnimationControls: React.FC<TabAnimationControlsProps> = ({
  state,
  progress,
  dispatch,
  tempo = 120,
  onClose
}) => {
  const handlePlayPause = () => {
    if (state.isPlaying) {
      dispatch({ type: 'PAUSE' });
    } else {
      dispatch({ type: 'PLAY' });
    }
  };

  const handleStop = () => {
    dispatch({ type: 'STOP' });
  };

  const handleSeek = (value: number[]) => {
    dispatch({ type: 'SEEK', position: value[0] / 100 });
  };

  const handleSpeedChange = (multiplier: number) => {
    dispatch({ type: 'SET_SPEED', speed: multiplier });
  };

  const handleToggleLoop = () => {
    dispatch({ type: 'TOGGLE_LOOP' });
  };

  const speedOptions = [
    { value: 0.25, label: '0.25x' },
    { value: 0.5, label: '0.5x' },
    { value: 0.75, label: '0.75x' },
    { value: 1.0, label: '1x' },
    { value: 1.5, label: '1.5x' },
    { value: 2.0, label: '2x' }
  ];

  const effectiveTempo = Math.round(tempo * state.speedMultiplier);

  return (
    <div className="space-y-4 p-4 border rounded-lg bg-card relative">
      {/* Close Button */}
      {onClose && (
        <Button
          variant="ghost"
          size="icon"
          className="absolute top-2 right-2 h-6 w-6"
          onClick={onClose}
          title="Close tab animation"
        >
          <X className="h-4 w-4" />
        </Button>
      )}

      {/* Progress Bar */}
      <div className="space-y-2">
        <Slider
          value={[progress * 100]}
          onValueChange={handleSeek}
          max={100}
          step={1}
          className="w-full"
          disabled={state.isPlaying}
        />
        <div className="flex justify-between text-xs text-muted-foreground">
          <span>Beat: {state.currentBeat.toFixed(2)}</span>
          <span>{Math.round(progress * 100)}%</span>
        </div>
      </div>

      {/* Main Controls */}
      <div className="flex items-center justify-center gap-2">
        <Button
          variant="outline"
          size="icon"
          onClick={handleStop}
          disabled={!state.isPlaying && state.currentBeat === 0}
          title="Stop and reset"
        >
          <Square className="h-4 w-4" />
        </Button>

        <Button
          variant="outline"
          size="icon"
          onClick={() => dispatch({ type: 'SEEK', position: 0 })}
          disabled={state.isPlaying}
          title="Skip to start"
        >
          <SkipBack className="h-4 w-4" />
        </Button>

        <Button
          size="lg"
          onClick={handlePlayPause}
          className="px-8"
        >
          {state.isPlaying ? (
            <>
              <Pause className="h-5 w-5 mr-2" />
              Pause
            </>
          ) : (
            <>
              <Play className="h-5 w-5 mr-2" />
              Play
            </>
          )}
        </Button>

        <Button
          variant={state.loop ? "default" : "outline"}
          size="icon"
          onClick={handleToggleLoop}
          title="Loop playback"
        >
          <Repeat className="h-4 w-4" />
        </Button>
      </div>

      {/* Speed and Tempo */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Gauge className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm font-medium">Speed</span>
          </div>
          <Badge variant="outline">
            {effectiveTempo} BPM
          </Badge>
        </div>

        <div className="flex gap-1 flex-wrap justify-center">
          {speedOptions.map(option => (
            <Button
              key={option.value}
              variant={state.speedMultiplier === option.value ? "default" : "outline"}
              size="sm"
              onClick={() => handleSpeedChange(option.value)}
              className="min-w-[60px]"
            >
              {option.label}
            </Button>
          ))}
        </div>
      </div>

      {/* Status Info */}
      <div className="flex justify-between text-xs text-muted-foreground pt-2 border-t">
        <span>Index: {state.currentIndex}</span>
        <span>
          {state.isPlaying ? (
            <span className="text-green-600 dark:text-green-400">● Playing</span>
          ) : (
            <span>● Paused</span>
          )}
        </span>
      </div>
    </div>
  );
};

export default TabAnimationControls;
